import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import DemoForm from "@/components/forms/DemoForm";

const faqs = [
  {
    question: "¿Qué es revenue management en hoteles?",
    answer: "El revenue management hotelero es una disciplina que utiliza análisis de datos y estrategias de precios para maximizar los ingresos del hotel. Consiste en vender la habitación correcta, al cliente correcto, en el momento correcto y al precio correcto."
  },
  {
    question: "¿Cómo funciona un sistema RMS?",
    answer: "Un sistema RMS (Revenue Management System) analiza datos históricos, tendencias del mercado, competencia y demanda para generar recomendaciones de precios óptimos. Los sistemas modernos utilizan inteligencia artificial para automatizar este proceso."
  },
  {
    question: "¿Cuánto puede aumentar mis ingresos el revenue management?",
    answer: "Los hoteles que implementan estrategias efectivas de revenue management típicamente ven incrementos del 5% al 20% en RevPAR. Con sistemas automatizados como Zimoo, algunos clientes han logrado aumentos superiores al 25%."
  },
  {
    question: "¿Necesito experiencia previa en revenue management?",
    answer: "No necesariamente. Zimoo está diseñado para ser intuitivo y fácil de usar. Ofrecemos capacitación completa y soporte continuo para que puedas aprovechar al máximo la plataforma sin importar tu nivel de experiencia."
  },
  {
    question: "¿Se integra con mi PMS actual?",
    answer: "Sí, Zimoo se integra con los principales sistemas de gestión hotelera (PMS) del mercado, incluyendo Opera, Cloudbeds, Mews, y muchos más. La integración es rápida y sin interrupciones en tu operación."
  },
  {
    question: "¿Es el revenue management solo para hoteles grandes?",
    answer: "No. El revenue management beneficia a hoteles de todos los tamaños. De hecho, los hoteles boutique y propiedades independientes pueden obtener ventajas competitivas significativas al implementar estas estrategias."
  },
];

export default function RevenueManagementPage() {
  useEffect(() => {
    document.title = "Revenue Management para Hoteles | Qué es y Cómo Funciona - Zimoo";
    
    // Add schema markup
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": faqs.map(faq => ({
        "@type": "Question",
        "name": faq.question,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": faq.answer
        }
      }))
    });
    document.head.appendChild(script);
    
    return () => {
      document.head.removeChild(script);
    };
  }, []);

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="section-padding bg-gradient-to-b from-[#FAFAFA] to-white grain-overlay">
        <div className="max-w-4xl mx-auto container-padding text-center relative z-10">
          <span className="label-text mb-4 block">GUÍA COMPLETA</span>
          <h1 className="heading-1 mb-6">
            Qué es Revenue Management en <span className="text-gradient">Hoteles</span>
          </h1>
          <p className="body-text text-lg max-w-2xl mx-auto">
            Descubre cómo el revenue management puede transformar la rentabilidad de tu hotel con estrategias probadas y tecnología avanzada.
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="section-padding bg-white">
        <div className="max-w-4xl mx-auto container-padding">
          <article className="prose prose-lg max-w-none">
            {/* Definition */}
            <div className="mb-12">
              <h2 className="heading-3 mb-4">Definición de Revenue Management Hotelero</h2>
              <p className="body-text mb-4">
                El <strong>revenue management hotelero</strong> (también conocido como gestión de ingresos) es una disciplina estratégica que utiliza análisis de datos, predicción de demanda y optimización de precios para maximizar los ingresos de un hotel.
              </p>
              <p className="body-text mb-4">
                En su esencia, el revenue management busca responder a la pregunta fundamental: <em>"¿Cómo puedo vender la habitación correcta, al cliente correcto, en el momento correcto, al precio correcto y a través del canal correcto?"</em>
              </p>
              
              {/* AEO Block */}
              <div className="bg-[#FAFAFA] rounded-xl p-6 border border-black/5 my-8">
                <h3 className="text-lg font-semibold text-[#0A0A0A] mb-2">¿Qué es revenue management?</h3>
                <p className="text-[#404040]">
                  El revenue management es la práctica de utilizar análisis de datos y precios dinámicos para maximizar los ingresos vendiendo el producto correcto al cliente correcto en el momento correcto. En hotelería, esto significa optimizar tarifas de habitaciones basándose en demanda, temporada y competencia.
                </p>
              </div>
            </div>

            {/* History */}
            <div className="mb-12">
              <h2 className="heading-3 mb-4">Historia y Evolución</h2>
              <p className="body-text mb-4">
                El revenue management nació en la industria aérea en los años 70 con American Airlines. La hotelería adoptó estas prácticas en los años 90, y desde entonces ha evolucionado significativamente con la llegada de la tecnología digital y la inteligencia artificial.
              </p>
              <p className="body-text">
                Hoy en día, el revenue management hotelero es una función crítica que combina ciencia de datos, economía del comportamiento y tecnología avanzada para tomar decisiones de precios en tiempo real.
              </p>
            </div>

            {/* Benefits */}
            <div className="mb-12">
              <h2 className="heading-3 mb-4">Beneficios del Revenue Management</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 my-6">
                {[
                  "Incremento del RevPAR entre 5% y 25%",
                  "Mejor ocupación en temporada baja",
                  "Maximización de ingresos en alta demanda",
                  "Decisiones basadas en datos, no intuición",
                  "Competitividad frente al mercado",
                  "Reducción de trabajo manual",
                  "Mejor comprensión del cliente",
                  "Optimización del mix de canales"
                ].map((benefit, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-[#404040]">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Key Concepts */}
            <div className="mb-12">
              <h2 className="heading-3 mb-4">Conceptos Clave del Revenue Management</h2>
              
              <h3 className="text-xl font-semibold text-[#0A0A0A] mb-3 mt-6">RevPAR (Revenue Per Available Room)</h3>
              <p className="body-text mb-4">
                El RevPAR es la métrica más importante en revenue management. Se calcula dividiendo los ingresos por habitaciones entre el total de habitaciones disponibles. Un RevPAR alto indica una combinación exitosa de ocupación y tarifa promedio.
              </p>
              <Link to="/revpar" className="inline-flex items-center text-[#1ABCB4] hover:underline font-medium">
                Aprende más sobre RevPAR <ArrowRight className="ml-1 h-4 w-4" />
              </Link>

              <h3 className="text-xl font-semibold text-[#0A0A0A] mb-3 mt-8">Pricing Dinámico</h3>
              <p className="body-text mb-4">
                El pricing dinámico ajusta las tarifas en tiempo real basándose en la demanda del mercado, eventos locales, comportamiento de la competencia y otros factores. Es el corazón de cualquier estrategia de revenue management moderna.
              </p>
              <Link to="/pricing-dinamico" className="inline-flex items-center text-[#1ABCB4] hover:underline font-medium">
                Conoce el pricing dinámico <ArrowRight className="ml-1 h-4 w-4" />
              </Link>

              <h3 className="text-xl font-semibold text-[#0A0A0A] mb-3 mt-8">Forecasting</h3>
              <p className="body-text mb-4">
                El forecasting o pronóstico de demanda permite anticipar cuántas habitaciones se venderán en el futuro. Con predicciones precisas, puedes ajustar precios proactivamente en lugar de reaccionar tarde a los cambios del mercado.
              </p>
              <Link to="/forecasting-hotelero" className="inline-flex items-center text-[#1ABCB4] hover:underline font-medium">
                Descubre el forecasting <ArrowRight className="ml-1 h-4 w-4" />
              </Link>
            </div>

            {/* Strategies */}
            <div className="mb-12">
              <h2 className="heading-3 mb-4">Estrategias de Revenue Management</h2>
              
              <h3 className="text-xl font-semibold text-[#0A0A0A] mb-3 mt-6">1. Segmentación de Mercado</h3>
              <p className="body-text mb-4">
                Identifica diferentes segmentos de clientes (corporativo, ocio, grupos) y desarrolla estrategias de precios específicas para cada uno. No todos los huéspedes tienen la misma sensibilidad al precio.
              </p>

              <h3 className="text-xl font-semibold text-[#0A0A0A] mb-3 mt-6">2. Gestión de Canales</h3>
              <p className="body-text mb-4">
                Optimiza tu presencia en diferentes canales de distribución (OTAs, sitio web directo, agencias) para maximizar ingresos mientras controlas costos de distribución.
              </p>

              <h3 className="text-xl font-semibold text-[#0A0A0A] mb-3 mt-6">3. Yield Management</h3>
              <p className="body-text mb-4">
                Aplica restricciones de estancia mínima, políticas de cancelación diferenciadas y tarifas no reembolsables para capturar demanda de alto valor.
              </p>

              <h3 className="text-xl font-semibold text-[#0A0A0A] mb-3 mt-6">4. Overbooking Controlado</h3>
              <p className="body-text mb-4">
                Implementa políticas de overbooking basadas en datos históricos de cancelaciones y no-shows para maximizar ocupación sin afectar la experiencia del huésped.
              </p>
            </div>

            {/* Real Examples */}
            <div className="mb-12">
              <h2 className="heading-3 mb-4">Ejemplos Reales de Revenue Management</h2>
              
              <div className="bg-[#FAFAFA] rounded-xl p-6 border border-black/5 my-6">
                <h4 className="font-semibold text-[#0A0A0A] mb-2">Caso: Hotel Boutique en Cancún</h4>
                <p className="text-[#404040] mb-3">
                  Un hotel boutique de 45 habitaciones implementó Zimoo para automatizar su estrategia de pricing. En 6 meses:
                </p>
                <ul className="space-y-2">
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    <span className="text-[#404040]">RevPAR aumentó 28%</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    <span className="text-[#404040]">Ocupación mejoró 18%</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    <span className="text-[#404040]">Reservas directas crecieron 35%</span>
                  </li>
                </ul>
              </div>
              
              <Link to="/casos-de-exito" className="inline-flex items-center text-[#1ABCB4] hover:underline font-medium">
                Ver más casos de éxito <ArrowRight className="ml-1 h-4 w-4" />
              </Link>
            </div>

            {/* Technology */}
            <div className="mb-12">
              <h2 className="heading-3 mb-4">Tecnología en Revenue Management</h2>
              <p className="body-text mb-4">
                Los sistemas modernos de revenue management (RMS) utilizan inteligencia artificial y machine learning para procesar grandes cantidades de datos y generar recomendaciones de precios en tiempo real.
              </p>
              <p className="body-text mb-4">
                Un buen RMS debe integrarse con tu PMS (sistema de gestión hotelera), channel manager y otras herramientas para crear un ecosistema conectado que automatice la optimización de ingresos.
              </p>
              <Link to="/software-rms" className="inline-flex items-center text-[#1ABCB4] hover:underline font-medium">
                Conoce el software RMS de Zimoo <ArrowRight className="ml-1 h-4 w-4" />
              </Link>
            </div>

            {/* LATAM Context */}
            <div className="mb-12">
              <h2 className="heading-3 mb-4">Revenue Management en México y LATAM</h2>
              <p className="body-text mb-4">
                El mercado hotelero latinoamericano tiene características únicas: alta estacionalidad, dependencia del turismo internacional, y competencia creciente. El revenue management adaptado a estas condiciones es fundamental para el éxito.
              </p>
              <p className="body-text mb-4">
                Destinos como Cancún, Riviera Maya, Los Cabos y Ciudad de México requieren estrategias sofisticadas que consideren eventos locales, temporadas de vacaciones y patrones de demanda específicos de la región.
              </p>
            </div>
          </article>

          {/* FAQs */}
          <div className="mt-16">
            <h2 className="heading-2 mb-8 text-center">Preguntas Frecuentes</h2>
            <Accordion type="single" collapsible className="w-full">
              {faqs.map((faq, index) => (
                <AccordionItem key={index} value={`item-${index}`}>
                  <AccordionTrigger className="text-left text-[#0A0A0A] font-medium">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-[#404040]">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-[#FAFAFA]">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="label-text mb-4 block">DA EL SIGUIENTE PASO</span>
              <h2 className="heading-2 mb-6">
                Implementa Revenue Management en tu Hotel
              </h2>
              <p className="body-text mb-6">
                Zimoo te ayuda a implementar estrategias de revenue management de clase mundial sin complicaciones. Solicita una demo y descubre cómo podemos aumentar tus ingresos.
              </p>
              <ul className="space-y-3">
                {[
                  "Plataforma intuitiva y fácil de usar",
                  "Integración con los principales PMS",
                  "Soporte en español 24/7",
                  "Resultados desde el primer mes"
                ].map((item, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <CheckCircle className="h-5 w-5 text-green-500" />
                    <span className="text-[#404040]">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <DemoForm sourcePage="revenue-management-hoteles" />
          </div>
        </div>
      </section>
    </div>
  );
}
